import { createContext, ReactElement, useMemo, useContext, useCallback } from 'react';
import useRestaurantData from '../../hooks/useRestaurantData';
import { AnnouncementInterface } from '../../types/AnnouncementInterface';
import { getAnnouncements } from '../../api/announcements';
import { removeAtIndex, replaceAtIndex } from '../../utils/general';

interface AnnouncementsContextInterface {
  hasError: boolean;
  isLoading: boolean;
  announcements: AnnouncementInterface[];
  loadAnnouncements: Function;
  addAnnouncement: Function;
  getAnnouncement: Function;
  removeAnnouncement: Function;
  updateAnnouncement: Function;
}

interface AnnouncementsProviderInterface {
  children: ReactElement;
}

const AnnouncementsContext = createContext<AnnouncementsContextInterface>({
  hasError: false,
  isLoading: true,
  announcements: null,
  loadAnnouncements: () => {},
  addAnnouncement: () => {},
  getAnnouncement: () => {},
  removeAnnouncement: () => {},
  updateAnnouncement: () => {}
});

const AnnouncementsProvider = ({ children }: AnnouncementsProviderInterface) => {
  const {
    data: announcements,
    setData: setAnnouncements,
    isLoading,
    hasError,
    load: loadAnnouncements
  } = useRestaurantData<AnnouncementInterface[]>(getAnnouncements);

  const addAnnouncement = useCallback(
    (announcement: AnnouncementInterface) => {
      const _announcements = announcements.slice();
      _announcements.push({ ...announcement } as AnnouncementInterface);
      setAnnouncements(_announcements);
    },
    [announcements, setAnnouncements]
  );

  const getAnnouncement = useCallback(
    (announcementID: number): AnnouncementInterface =>
      announcements.find((announcement) => announcement.announcementID === announcementID),
    [announcements]
  );

  const removeAnnouncement = useCallback(
    (announcementID: number) => {
      const announcementIndex: number = announcements.findIndex((section) => section.announcementID === announcementID);
      setAnnouncements(removeAtIndex(announcements, announcementIndex));
    },
    [announcements, setAnnouncements]
  );

  const updateAnnouncement = useCallback(
    (announcementID: number, announcement: AnnouncementInterface) => {
      const announcementIndex: number = announcements.findIndex((section) => section.announcementID === announcementID);
      const _announcement: AnnouncementInterface = { ...announcements[announcementIndex], ...announcement };

      setAnnouncements(replaceAtIndex(announcements, announcementIndex, _announcement));
    },
    [announcements, setAnnouncements]
  );

  const providerValue = useMemo(
    () => ({
      hasError,
      isLoading,
      announcements,
      loadAnnouncements,
      addAnnouncement,
      getAnnouncement,
      removeAnnouncement,
      updateAnnouncement
    }),
    [
      hasError,
      isLoading,
      announcements,
      loadAnnouncements,
      addAnnouncement,
      getAnnouncement,
      removeAnnouncement,
      updateAnnouncement
    ]
  );
  return <AnnouncementsContext.Provider value={providerValue}>{children}</AnnouncementsContext.Provider>;
};

const useAnnouncementsContext = () => {
  const context = useContext(AnnouncementsContext);
  if (!context) {
    throw new Error(`AnnouncementsContext must be used within the AnnouncementsProvider component`);
  }
  return context;
};

export { AnnouncementsProvider as default, useAnnouncementsContext };
